import type { CatalogJobListItem } from "@tabbio/types";
import { useTranslation } from "react-i18next";
import { MaterialSymbol } from "../icons/MaterialSymbol";
import { JobLogo } from "./JobLogo";
import {
  formatRelativeDate,
  getCatalogJobDisplayDate,
  humanizeJobValue,
} from "../../lib/jobs-format";

export function CatalogJobCard({
  item,
  applied: _applied,
  onPress,
  onToggleSaved,
  onHide,
  isSaving = false,
  isHiding = false,
  index = 0,
  totalCount = 1,
}: {
  item: CatalogJobListItem;
  applied: boolean;
  onPress: () => void;
  onToggleSaved?: () => void;
  onHide?: () => void;
  isSaving?: boolean;
  isHiding?: boolean;
  index?: number;
  totalCount?: number;
  distanceKm?: number | null;
}) {
  const { t, i18n } = useTranslation("seeker");
  const isLast = index >= totalCount - 1;
  const isSaved = item.userState?.saved === true;
  const companyLogo = item.company.logo?.trim() || null;
  const companyLine =
    [item.company.name, item.location].filter(Boolean).join(", ") ||
    t("jobs.locationFallback");
  const tags = [
    item.type ? humanizeJobValue(item.type) : null,
    item.workMode ? humanizeJobValue(item.workMode) : null,
  ].filter(Boolean) as string[];
  const sourceLabel = item.sourceInfo?.label ?? "";
  const timeAgo = formatRelativeDate(
    getCatalogJobDisplayDate(item),
    i18n.language,
  );
  const sourceText =
    sourceLabel && timeAgo
      ? `via ${sourceLabel}, ${timeAgo}`
      : sourceLabel
        ? `via ${sourceLabel}`
        : timeAgo;
  const sourceIconName =
    item.source === "google_jobs_dataforseo"
      ? "search"
      : item.source === "tabbio_native"
        ? "work"
        : item.source === "ats_workable"
          ? "business_center"
          : "open_in_new";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onPress}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onPress();
        }
      }}
      className="tabbio-list-row"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        minHeight: 96,
        padding: "20px 16px",
        marginBottom: 0,
        border: 0,
        borderBottom: isLast
          ? "none"
          : "1px solid var(--tabbio-border-subtle, var(--input-border))",
        borderRadius: 0,
        background: "var(--bg)",
        cursor: "pointer",
      }}
    >
      <JobLogo uri={companyLogo} />

      <div
        className="tabbio-list-row-body"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: 1,
          minWidth: 0,
        }}
      >
        <div
          className="tabbio-list-row-title"
          style={{
            fontSize: 16,
            lineHeight: "20px",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {item.title}
        </div>
        <div
          className="tabbio-list-row-sub"
          style={{
            fontSize: 14,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {companyLine}
        </div>

        {tags.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            {tags.map((tag) => (
              <span
                key={tag}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: 28,
                  borderRadius: 20,
                  border:
                    "1px solid var(--tabbio-border-subtle, var(--input-border))",
                  background: "var(--tabbio-surface, var(--surface))",
                  padding: "5px 8px",
                  color: "var(--text-secondary)",
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: "16px",
                  opacity: 0.8,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        {sourceText ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              minHeight: 18,
              color: "var(--text-secondary)",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            <MaterialSymbol
              name={sourceIconName}
              size={16}
              style={{ color: "var(--text-tertiary)", flexShrink: 0 }}
            />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {sourceText}
            </span>
          </div>
        ) : null}
      </div>

      {onToggleSaved || onHide ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            flexShrink: 0,
          }}
        >
          {onHide ? (
            <button
              type="button"
              aria-label={t("jobs.actions.hide")}
              disabled={isHiding}
              onClick={(event) => {
                event.stopPropagation();
                onHide();
              }}
              style={{
                width: 36,
                height: 36,
                border: 0,
                borderRadius: 999,
                background: "transparent",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isHiding ? "default" : "pointer",
                opacity: isHiding ? 0.35 : 1,
              }}
            >
              <MaterialSymbol
                name="close"
                size={18}
                style={{ color: "var(--text-tertiary)" }}
              />
            </button>
          ) : null}
          {onToggleSaved ? (
            <button
              type="button"
              aria-label={
                isSaved ? t("jobs.actions.unsave") : t("jobs.actions.save")
              }
              disabled={isSaving}
              onClick={(event) => {
                event.stopPropagation();
                onToggleSaved();
              }}
              style={{
                width: 36,
                height: 36,
                border: 0,
                borderRadius: 999,
                background: "transparent",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isSaving ? "default" : "pointer",
                opacity: isSaving ? 0.35 : 1,
              }}
            >
              <MaterialSymbol
                name="bookmark"
                size={18}
                style={{
                  color: isSaved
                    ? "var(--text-primary)"
                    : "var(--text-tertiary)",
                  fontVariationSettings: isSaved
                    ? '"FILL" 1, "wght" 400, "GRAD" 0, "opsz" 24'
                    : '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24',
                }}
              />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
